import z from 'zod';
import { fileSchema } from '../File/schemas';
import { languageCodeSchema } from '../Language/schemas';

export const Settings = {
	CustomerTheme: 'customer-theme',
	UserData: 'user-data',
	EventData: 'event-data',
	CompanyPortal: 'company-portal',
	PersonalDashboard: 'personal-dashboard',
	ContactPersons: 'contact-persons',
	Calendar: 'calendar',
	Notifications: 'notifications',
	Monitoring: 'monitoring',
	MailServer: 'mail-server',
	AccessApp: 'access-app',
	License: 'license',
	FeatureFlags: 'feature-flags',
	SSO: 'auth-sso',
	Checkinatwork: 'checkinatwork',
	Playit: 'playit',
	TenantSync: 'tenant-sync',
	Support: 'support',
	PartnerPortal: 'partner-portal',
	PrincipalGroupLabels: 'principal-group-labels',
	ApiCredentials: 'api-credentials',
	Languages: 'languages',
	AdminBrand: 'admin-brand',
	General: 'general',
	DemoData: 'demo-data',
	Screening: 'screening',
	TenantConfig: 'tenant-config',
	GroupData: 'group-data',
	EventNotificationToggles: 'notification-toggles',
	MicrosoftGraph: 'ms-graph',
	OutlookAddin: 'outlook-addin',
	CheckBlockListJob: 'check-block-list-job',
	AgenticParsing: 'agentic-parsing',
} as const;

export type SettingsKey = (typeof Settings)[keyof typeof Settings];

export const tenantModeSchema = z.enum(['simple', 'advanced', 'legacy']);

export const tenantFlagsSchema = z.object({
	USES_9ID_HARDWARE: z.boolean().optional(),
	USES_SUPERVISORS: z.boolean().optional(),
	USES_BIOMETRY: z.boolean().optional(),
	USES_EMERGENCY: z.boolean().optional(),
	USES_RANDOM_CHECKS: z.boolean().optional(),
	USES_ANTI_PASSBACK: z.boolean().optional(),
	USES_CIAW: z.boolean().optional(),
	USES_COMPANY_CHECKS: z.boolean().optional(),
	USES_SUBCONTRACTING: z.boolean().optional(),
	USES_TAGS: z.boolean().optional(),
	USES_WEBHOOKS: z.boolean().optional(),
	USES_EXTERNAL_ACCESS: z.boolean().optional(),
	// AIGIS (Belgian port-ban / havenverbod) screening. Enabled by 9ID only once the
	// tenant has a signed NAMB agreement (port facilities only, Scheepvaartwetboek
	// art. 2.5.2.81). Gates whether an AIGIS hit is acted on and whether the AIGIS
	// row is configurable in screening settings.
	USES_AIGIS: z.boolean().optional(),
});

export const settingSchema = <T extends z.ZodType<any, z.ZodTypeDef, any>>(settings: T) =>
	z
		.object({
			label: z.string().optional(),
			key: z.string().optional(),
			template: z.any(),
			settings: settings as T,
			isProtected: z.boolean().optional(),
			createdAt: z.coerce.date().optional(),
			updatedAt: z.coerce.date().optional(),
		})
		.describe('Setting');

const linkObject = z.object({
	label: z.string().optional(),
	url: z.string().optional(),
});

export const generalSettingSchema = settingSchema(
	z.object({
		defaultCompanyPortalType: z.string().optional(),
		/** @deprecated Renamed to `defaultCompanyPortalType`; may still exist in older documents. */
		defaultCompanyType: z.string().optional(),
		expiryNotificationDays: z.number().optional(),
		supportEnabled: z.boolean().optional(),
	}),
).describe('GeneralSetting');
export const personalDashboardSettingSchema = settingSchema(
	z.object({
		welcomeMessage: z.string().optional(),
		links: z.array(linkObject).optional(),
		helpLinks: z.array(linkObject).optional(),
	}),
).describe('PersonalDashboardSetting');

const languageContentSchema = z.object({
	Links: z.array(linkObject).optional(),
	welcomeMessage: z.string().optional(),
});

export const accessAppSettingSchema = settingSchema(
	z.object({
		languages: z.object({
			en: z.boolean(),
			nl: z.boolean().optional(),
			fr: z.boolean().optional(),
			de: z.boolean().optional(),
			pl: z.boolean().optional(),
		}),
		en: languageContentSchema,
		nl: languageContentSchema.optional(),
		fr: languageContentSchema.optional(),
		de: languageContentSchema.optional(),
		pl: languageContentSchema.optional(),
	}),
).describe('accessAppSetting');

export const companyPortalSettingSchema = settingSchema(
	z
		.object({
			welcomeMessage: z.string().optional(),
			links: z.array(z.object({ label: z.string().optional(), url: z.string().optional() })).optional(),
			helpLinks: z.array(z.object({ label: z.string().optional(), url: z.string().optional() })).optional(),
		})
		.optional(),
).describe('CompanyPortalSetting');

export const customerThemeSettingSchema = settingSchema(
	z.object({
		panorama: z.lazy(() => fileSchema).nullable(),
		logo: z.lazy(() => fileSchema).nullable(),
		logoPadding: z.number().optional(),
		colors: z.object({
			accent: z.string().optional(),
			logoBackground: z.string().optional(),
			text: z.string().optional(),
		}),
		companyName: z.string().optional(),
	}),
).describe('CustomerThemeSetting');

export const propertyTypeSchema = z.enum(['short-string', 'description', 'license-plate', 'number', 'date', 'boolean', 'select']);

/**
 * Custom property name. Shared by the user/group/event property schemas.
 * Trimmed, non-empty, and may not contain a `.`: property values are addressed by
 * dotted paths (Mongo update operators, lodash `_.get` in report columns), which
 * interpret a `.` as a nested-path separator.
 */
export const propertyNameSchema = z
	.string()
	.trim()
	.min(1, 'Property name is required')
	.refine((name) => !name.includes('.'), {
		message: 'Property name may not contain a "." character',
	});

export const eventPropertySchema = z.object({
	name: propertyNameSchema,
	type: propertyTypeSchema,
	// Choices for the 'select' (dropdown) type. Ignored by other types. Shared by
	// the user/group/event property schemas via the aliases below.
	options: z.array(z.string()).optional(),
});

export const userPropertySchema = eventPropertySchema;

export const groupPropertySchema = eventPropertySchema;

/** @deprecated use propertyTypeSchema */
export const eventPropertyTypeSchema = propertyTypeSchema;

export const userDataSettingSchema = settingSchema(
	z.object({
		// Custom user properties, unified to {name,type}[] (same shape as group/event).
		// The legacy split shape ({ extraUserProps: string[], extraUserPropsTypes }) is gone.
		extraUserProps: z.array(userPropertySchema),
		showShortId: z.boolean().optional(),
		flaggedCountries: z.array(z.string()).optional(),
		blockedCountries: z.array(z.string()).optional(),
		minAge: z.number().optional(),
		maxAge: z.number().optional(),
		externalZoneAccessUrl: z.string().optional(),
	}),
).describe('UserDataSetting');

export const eventDataSettingSchema = settingSchema(
	z.object({
		extraEventProps: z.array(eventPropertySchema),
	}),
).describe('EventDataSetting');

export const groupDataSettingSchema = settingSchema(
	z.object({
		extraGroupProps: z.array(groupPropertySchema),
	}),
).describe('GroupDataSetting');

export const contactPersonsSettingSchema = settingSchema(
	z.object({
		emailDomains: z.array(z.string()),
	}),
).describe('ContactPersonsSetting');

export const calendarSettingsSchema = settingSchema(
	z.object({
		domainAllowList: z.array(z.string()).optional(),
		domainIgnoreList: z.array(z.string()).optional(),
		eventType: z.string().optional(),
		/** @deprecated Moved to `eventType.allowMinutesPre/Post`; may still exist in older documents. */
		allowMinutesPre: z.number().optional(),
		/** @deprecated Moved to `eventType.allowMinutesPre/Post`; may still exist in older documents. */
		allowMinutesPost: z.number().optional(),
	}),
).describe('CalendarSettings');

export const demoDataSettingsSchema = settingSchema(z.any()).describe('DemoDataSetting');

export const notificationsSettingsSchema = settingSchema(
	z.object({
		checkIn: z.boolean().optional(),
		checkOut: z.boolean().optional(),
		checkInOutExtraRecipients: z.array(z.string()).optional(),
		checkInOutTextMessages: z.boolean().optional(),
		checkInTeamsMessages: z.boolean().optional(),
		ignoreDemoWhitelist: z.boolean().optional(),
		eventGroupAdded: z.boolean().optional(),
		documentRejected: z.boolean().optional(),
		/** @deprecated Moved to `general.expiryNotificationDays`; may still exist in older documents. */
		expiryWarningInterval: z.number().optional(),
	}),
).describe('NotificationsSetting');

export const monitoringSettingsSchema = settingSchema(z.record(z.string(), z.any())).describe('MonitoringSetting');

export const mailServerSettingsSchema = settingSchema(
	z.object({
		method: z.enum(['api', 'smtp', 'microsoftGraph']).optional().default('api'),
		smtp: z.boolean().optional(),
		from: z.string().optional(),
		host: z.string().optional(),
		port: z.number().optional(),
		auth: z
			.object({
				user: z.string(),
				pass: z.string(),
			})
			.optional(),
		// Deprecated — Graph credentials now live in the `ms-graph` settings doc.
		// Kept here until apps/manager's mail-server form is migrated; remove after that.
		tenantId: z.string().optional(),
		clientId: z.string().optional(),
		clientSecret: z.string().optional(),
		senderEmail: z.string().optional(),
	}),
).describe('MailServerSetting');

// Microsoft Graph credentials — application registration in the tenant's Azure AD.
// Used by Graph-based email (when mail-server.method === 'microsoftGraph') and by
// Microsoft Teams 1:1 chat notifications. senderEmail doubles as the service-account
// UPN for both flows.
export const microsoftGraphSettingsSchema = settingSchema(
	z.object({
		tenantId: z.string().optional(),
		clientId: z.string().optional(),
		clientSecret: z.string().optional(),
		senderEmail: z.string().optional(),
		// Teams app (catalog) ID — distinct from clientId. Used to build the Teams deep link
		// in the activity-notification topic.webUrl. Each tenant gets its own when it packages
		// the Teams app (see docs/customers/ESA/teams-app).
		teamsAppId: z.string().optional(),
	}),
).describe('MicrosoftGraphSetting');

export const licenseSettingSchema = settingSchema(z.any()).describe('LicenseSetting');

export const featureFlagsSchema = z.object({
	USES_RFID: z.boolean().optional(),
	USES_RSID: z.boolean().optional(),
	USES_RANDOM_CHECKS: z.boolean().optional(),
	USES_EMERGENCY_MODULE: z.boolean().optional(),
	AUTO_CHECKIN: z.boolean().optional(),
	USES_RFID_ENC: z.boolean().optional(),
	USES_MISTRAL: z.boolean().optional(),
	COMPANY_PORTAL_REMOTE_ACTIONS: z.boolean().optional(),
	USES_COMPANY_API_CHECKS: z.boolean().optional(),
	USES_SUBCONTRACTING: z.boolean().optional(),
	USES_TAGS: z.boolean().optional(),
	USE_TENANT_SYNC: z.boolean().optional(),
	USE_CHECKINATWORK: z.boolean().optional(),
	USES_PLAYIT: z.boolean().optional(),
	IS_ALHEEMBOUW: z.boolean().optional(),
	USES_WEBHOOKS: z.boolean().optional(),
	USES_SUPERVISOR: z.boolean().optional(),
	USES_EXTERNAL_ZONE_ACCESS: z.boolean().optional(),
	USES_PARTNER_PORTAL: z.boolean().optional(),
	DEFAULT_ADMIN_EXPERIENCE: z.enum(['legacy', '2.0']).optional(),
	ADMIN_EXPERIENCE_2_0_ONLY: z.boolean().optional(),
	USES_VCA_SELF_EMPLOYED: z.boolean().optional(),
	USES_ANTI_PASSBACK: z.boolean().optional(),
	IS_BASF: z.boolean().optional(),
	USES_OWNER_EMAIL_DOMAIN_CHECK: z.boolean().optional(),
	USES_WORKFORCE: z.boolean().optional(),
});

export const featureFlagsSettingSchema = settingSchema(featureFlagsSchema).describe('FeatureFlagsSetting');

export const ssoSettingsSchema = settingSchema(
	z.object({
		samlIdpXml: z.string().optional(),
		provisionNewUsers: z.boolean().optional(),
		defaultRoleId: z.string().optional(),
		strictRoles: z.boolean().optional(),
		groupRoleMapping: z.record(z.string(), z.string()).optional(),
	}),
).describe('SSOSetting');

export const adminBrandSettingSchema = settingSchema(
	z.object({
		primaryColor: z.string().optional(),
	}),
).describe('AdminBrandSetting');

export const supportSettingSchema = settingSchema(
	z.object({
		enabled: z.boolean().optional(),
	}),
).describe('SupportSetting');

export const checkinatworkSettingsSchema = settingSchema(
	z.object({
		enabled: z.boolean().optional(),
		publicKey: z.string().nullable().optional(),
		privateKey: z.string().nullable().optional(),
		certificate: z.string().nullable().optional(),
		zoneMapping: z.record(z.string(), z.string()).optional(),
		expeditorNumber: z.string().nullable().optional(),
	}),
).describe('CheckinatworkSetting');

export const playitSettingSchema = settingSchema(
	z.object({
		organizationId: z.string().optional(),
		apiUrl: z.string().optional(),
		redirectUrl: z.string().optional(),
		email: z.string().optional(),
		password: z.string().optional(),
		clientId: z.string().optional(),
		clientSecret: z.string().optional(),
	}),
).describe('PlayitSetting');


export const tenantSyncSettingsSchema = settingSchema(
	z.object({
		enabled: z.boolean().optional(),
		debug: z.boolean().optional(),
		baseUrl: z.string().optional(),
		clientId: z.string().optional(),
		clientSecret: z.string().optional(),
		syncRoleIds: z.array(z.string()).optional(),
		lastSyncTimes: z
			.object({
				users: z.string().optional(),
				events: z.string().optional(),
				accessLogs: z.string().optional(),
			})
			.optional(),
	}),
).describe('TenantSyncSetting');

export const partnerPortalSettingSchema = settingSchema(
	z
		.object({
			welcomeMessage: z.string().optional(),
			links: z.array(z.object({ label: z.string().optional(), url: z.string().optional() })).optional(),
			helpLinks: z.array(z.object({ label: z.string().optional(), url: z.string().optional() })).optional(),
		})
		.optional(),
).describe('PartnerPortalSetting');

export const principalGroupLabelsSettingsSchema = settingSchema(
	z.object({
		principalGroupLabels: z
			.array(
				z.object({
					value: z.string(),
				}),
			)
			.optional(),
	}),
).describe('PrincipalGroupLabelsSetting');

export const apiCredentialsSettingsSchema = settingSchema(
	z.object({
		credentials: z
			.array(
				z.object({
					clientId: z.string(),
					type: z.string().optional(),
					createdAt: z.string().optional(),
				}),
			)
			.optional(),
	}),
).describe('ApiCredentialsSetting');

export const languagesSettingSchema = settingSchema(
	z.object({
		defaultLanguage: languageCodeSchema,
		enabledLanguages: z.array(languageCodeSchema),
	}),
).describe('LanguagesSetting');

// What a screening match does, per source category.
export const screeningBehaviourSchema = z.enum(['block', 'review', 'off']);

export const screeningSettingSchema = settingSchema(
	z.object({
		enabled: z.boolean().default(false),
		// Local databases (Belgian port bans / AIGIS). Default: block.
		localBehaviour: screeningBehaviourSchema.default('block'),
		// International sanctions / watchlist / debarment lists. Default: review.
		sanctionsBehaviour: screeningBehaviourSchema.default('review'),
		// Run the nightly sweep that screens all active users via the Person API.
		nightlySyncEnabled: z.boolean().optional(),
		// How long a live screening result is cached before re-checking.
		liveCacheTtlMinutes: z.number().optional(),
	}),
).describe('ScreeningSetting');

export const tenantConfigSettingSchema = settingSchema(
	z.object({
		mode: tenantModeSchema.optional(),
		flags: tenantFlagsSchema.optional(),
	}),
).describe('TenantConfigSetting');

// Per-template opt-ins for event-related notifications. All keys default to undefined →
// disabled; the customer flips the relevant switch in admin → Settings → Notifications.
// Doc key is 'notification-toggles'; covers visit & job notifications.
export const eventNotificationTogglesSchema = settingSchema(
	z.object({
		eventCreated: z.boolean().optional(),
		eventCancelled: z.boolean().optional(),
		eventUpdated: z.boolean().optional(),
		eventHostAssigned: z.boolean().optional(),
		eventReminder24h: z.boolean().optional(),
		userReviewFlagged: z.boolean().optional(),
		userReviewCompleted: z.boolean().optional(),
		// 9ID-1867 additions
		eventBadgeHolderConfirmation: z.boolean().optional(),
		contractorPortalAccess: z.boolean().optional(),
		registrationCleared: z.boolean().optional(),
		eventReminder5wd: z.boolean().optional(),
		wifiCredentials: z.boolean().optional(),
		eventCheckoutVisitor: z.boolean().optional(),
		/** Role ids whose holders count as badge holders — a permanent on-site badge means a
		 * simplified confirmation instead of a registration invite. */
		badgeHolderRoleIds: z.string().array().optional(),
	}),
).describe('EventNotificationTogglesSetting');

export const outlookAddinSettingSchema = settingSchema(
	z.object({
		enabled: z.boolean().optional(),
		internalDomains: z.array(z.string()).optional(),
		displayName: z.string().optional(),
		buttonLabel: z.string().optional(),
		bannerText: z.string().optional(),
		logo: z
			.lazy(() => fileSchema)
			.nullable()
			.optional(),
	}),
).describe('OutlookAddinSetting');

/** Worker flag: when true, the fifteen-minute block-list job re-evaluates all users. */
export const checkBlockListJobSettingSchema = settingSchema(
	z.object({
		shouldRun: z.boolean().optional(),
	}),
).describe('CheckBlockListJobSetting');

// Global switch (NineID-internal) for the agentic document parsing rollout. Flipping `enabled` runs a
// reversible background conversion: it flips DocumentType.parsingMethod for the identity + SocSec-BE
// types to 'agentic' (and back) and converts the affected task templates/forms to the agentic shape.
// `status` is the state machine the admin page polls; `warnings` surfaces degraded/driving/deferred cases.
export const agenticParsingSettingSchema = settingSchema(
	z.object({
		enabled: z.boolean().default(false),
		status: z.enum(['idle', 'applying', 'reverting', 'failed']).default('idle'),
		lastRunAt: z.string().optional(),
		lastError: z.string().optional(),
		counts: z
			.object({
				docTypes: z.number(),
				templates: z.number(),
				tasks: z.number(),
			})
			.partial()
			.optional(),
		warnings: z.array(z.string()).optional(),
	}),
).describe('AgenticParsingSetting');

export const settingsSchemasMap: Record<SettingsKey, z.ZodSchema> = {
	[Settings.CustomerTheme]: customerThemeSettingSchema,
	[Settings.UserData]: userDataSettingSchema,
	[Settings.EventData]: eventDataSettingSchema,
	[Settings.CompanyPortal]: companyPortalSettingSchema,
	[Settings.PersonalDashboard]: personalDashboardSettingSchema,
	[Settings.ContactPersons]: contactPersonsSettingSchema,
	[Settings.Calendar]: calendarSettingsSchema,
	[Settings.Notifications]: notificationsSettingsSchema,
	[Settings.Monitoring]: monitoringSettingsSchema,
	[Settings.MailServer]: mailServerSettingsSchema,
	[Settings.AccessApp]: accessAppSettingSchema,
	[Settings.License]: licenseSettingSchema,
	[Settings.FeatureFlags]: featureFlagsSettingSchema,
	[Settings.SSO]: ssoSettingsSchema,
	[Settings.Checkinatwork]: checkinatworkSettingsSchema,
	[Settings.TenantSync]: tenantSyncSettingsSchema,
	[Settings.PartnerPortal]: partnerPortalSettingSchema,
	[Settings.PrincipalGroupLabels]: principalGroupLabelsSettingsSchema,
	[Settings.ApiCredentials]: apiCredentialsSettingsSchema,
	[Settings.Languages]: languagesSettingSchema,
	[Settings.Support]: supportSettingSchema,
	[Settings.Playit]: playitSettingSchema,
	[Settings.AdminBrand]: adminBrandSettingSchema,
	[Settings.General]: generalSettingSchema,
	[Settings.DemoData]: demoDataSettingsSchema,
	[Settings.Screening]: screeningSettingSchema,
	[Settings.TenantConfig]: tenantConfigSettingSchema,
	[Settings.GroupData]: groupDataSettingSchema,
	[Settings.EventNotificationToggles]: eventNotificationTogglesSchema,
	[Settings.MicrosoftGraph]: microsoftGraphSettingsSchema,
	[Settings.OutlookAddin]: outlookAddinSettingSchema,
	[Settings.CheckBlockListJob]: checkBlockListJobSettingSchema,
	[Settings.AgenticParsing]: agenticParsingSettingSchema,
} as const;
